import React, { FC, useMemo, useRef } from "react";
import { Country } from "@lookiero/sty-psp-locale";
import {
  CheckboxQuestionItem,
  HostDateQuestionItem,
  HostDefaultQuestionItem,
  HostHeightImperialQuestionItem,
  HostHeightMetricQuestionItem,
  HostRankQuestionItem,
  HostSelectQuestionItem,
  HostWeightImperialQuestionItem,
  HostWeightMetricQuestionItem,
  ImageQuestionItem,
  OptionQuestionItem,
  PhotosDescription,
  Question,
  QuestionDescriptionId,
  QuestionDescriptionProvider,
  QuestionDescriptions,
  QuestionItemProvider,
  QuestionItems as QuestionItemsType,
  QuestionType,
  RoundedButtonQuestionItem,
  RoundedButtonWithIconOnSelectedQuestionItem,
  SilhouetteGuide,
  SizeGuide,
  SquaredButtonQuestionItem,
  StrikeOutAvoidButtonWithIconQuestionItem,
  StrikeOutAvoidButtonQuestionItem,
  StyleDescription,
  HostInputPillsQuestionItem,
  StrikeOutButtonWithColorQuestionItem,
  StrikeOutButtonWithPrintQuestionItem,
  ScreenProjection,
  ButtonWithIconQuestionItem,
  AvoidImageQuestionItem,
  FitButtonWithIconQuestionItem,
  HostPriceSliderQuestionItem,
  ItemsLookingForHint,
  SkinQuestionItem,
  InspirationDescription,
} from "@lookiero/sty-psp-quiz-style-profile-common-ui";
import { Segment } from "@lookiero/sty-psp-segment";
import { Tradename } from "@lookiero/sty-sp-tradename";
import { HostImageUploadQuestionItem } from "../../../../components/organisms/question/components/hostImageUploadQuestionItem/HostImageUploadQuestionItem";
import { HostScreenOnSuccessFunction } from "../../../../components/organisms/question/components/hostScreenCta/HostScreenCta";
import { StyleProfileSocialDescription } from "../../../../components/organisms/question/components/hostScreenDescription/components /styleProfileSocialDescription/StyleProfileSocialDescription";
import { HostScreenQuestionItemHOC } from "../../../../components/organisms/question/components/hostScreenQuestionItem/HostScreenQuestionItem";
import { HostScreenWithSummaryQuestionItemHOC } from "../../../../components/organisms/question/components/hostScreenWithSummaryQuestionItem/HostScreenWithSummaryQuestionItem";
import { SocialNetworksInputQuestionItem } from "../../../../components/organisms/question/components/socialNetworksInputQuestionItem/SocialNetworksInputQuestionItem";
import { DOMAIN, PREFIX } from "../../../../i18n/i18n";

const baseQuestionItems: QuestionItemsType = {
  [QuestionType.HOST_DEFAULT]: HostDefaultQuestionItem,
  [QuestionType.HOST_SCREEN]: () => null,
  [QuestionType.HOST_SCREEN_WITH_SUMMARY]: () => null,
  [QuestionType.IMAGE]: ImageQuestionItem,
  [QuestionType.ROUNDED_BUTTON]: RoundedButtonQuestionItem,
  [QuestionType.ROUNDED_BUTTON_WITH_ICON_ON_SELECTED]: RoundedButtonWithIconOnSelectedQuestionItem,
  [QuestionType.SQUARED_BUTTON]: SquaredButtonQuestionItem,
  [QuestionType.HOST_SELECT]: HostSelectQuestionItem,
  [QuestionType.OPTION]: OptionQuestionItem,
  [QuestionType.HOST_HEIGHT_METRIC]: HostHeightMetricQuestionItem,
  [QuestionType.HOST_WEIGHT_METRIC]: HostWeightMetricQuestionItem,
  [QuestionType.HOST_HEIGHT_IMPERIAL]: HostHeightImperialQuestionItem,
  [QuestionType.HOST_WEIGHT_IMPERIAL]: HostWeightImperialQuestionItem,
  [QuestionType.HOST_RANK]: HostRankQuestionItem,
  [QuestionType.CHECKBOX]: CheckboxQuestionItem,
  [QuestionType.HOST_DATE]: HostDateQuestionItem,
  [QuestionType.HOST_IMAGE_UPLOAD]: HostImageUploadQuestionItem,
  [QuestionType.SOCIAL_NETWORKS_INPUT]: SocialNetworksInputQuestionItem,
  [QuestionType.STRIKE_OUT_AVOID_BUTTON_WITH_ICON]: StrikeOutAvoidButtonWithIconQuestionItem,
  [QuestionType.STRIKE_OUT_AVOID_BUTTON]: StrikeOutAvoidButtonQuestionItem,
  [QuestionType.HOST_INPUT_PILLS]: HostInputPillsQuestionItem,
  [QuestionType.STRIKE_OUT_BUTTON_WITH_COLOR]: StrikeOutButtonWithColorQuestionItem,
  [QuestionType.STRIKE_OUT_BUTTON_WITH_PRINT]: StrikeOutButtonWithPrintQuestionItem,
  [QuestionType.BUTTON_WITH_ICON]: ButtonWithIconQuestionItem,
  [QuestionType.FIT_BUTTON_WITH_ICON]: FitButtonWithIconQuestionItem,
  [QuestionType.AVOID_IMAGE]: AvoidImageQuestionItem,
  [QuestionType.HOST_PRICE_SLIDER]: HostPriceSliderQuestionItem,
  [QuestionType.SLIDER_OPTION]: () => null,
  [QuestionType.SKIN]: SkinQuestionItem,
  [QuestionType.NUMBER_WITH_CURRENCY]: () => null,
};

const questionDescriptions: Partial<QuestionDescriptions> = {
  [QuestionDescriptionId.STYLE_PROFILE_SIZE_GUIDE]: SizeGuide,
  [QuestionDescriptionId.STYLE_PROFILE_SILHOUETTE_GUIDE]: SilhouetteGuide,
  [QuestionDescriptionId.STYLE_PROFILE_STYLE_DESCRIPTION]: StyleDescription,
  [QuestionDescriptionId.STYLE_PROFILE_PHOTOS_DESCRIPTION]: PhotosDescription,
  [QuestionDescriptionId.STYLE_PROFILE_SOCIAL_DESCRIPTION]: StyleProfileSocialDescription,
  [QuestionDescriptionId.STYLE_PROFILE_ITEMS_LOOKING_FOR_HINT]: ItemsLookingForHint,
  [QuestionDescriptionId.STYLE_PROFILE_INSPIRATION_DESCRIPTION]: InspirationDescription,
};

interface QuestionFormProps {
  readonly question: ScreenProjection;
  readonly tradename: Tradename;
  readonly segment: Segment;
  readonly country: Country;
  readonly androidInAppBrowser?: boolean;
  readonly onOpenSilhouetteGuide: () => void;
  readonly onOpenSizeGuide: () => void;
  readonly onSuccess: HostScreenOnSuccessFunction;
}

const QuestionForm: FC<QuestionFormProps> = ({
  question,
  tradename,
  segment,
  country,
  androidInAppBrowser = false,
  onOpenSilhouetteGuide,
  onOpenSizeGuide,
  onSuccess,
}) => {
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  /**
   * Memoized questionItems so they are not re-created on each re-render.
   *
   * Passing onSuccessRef will avoid any issues with onSuccess callback memoization.
   */
  const questionItems = useMemo(
    () => ({
      ...baseQuestionItems,
      [QuestionType.HOST_SCREEN]: HostScreenQuestionItemHOC({ onSuccess: onSuccessRef.current }),
      [QuestionType.HOST_SCREEN_WITH_SUMMARY]: HostScreenWithSummaryQuestionItemHOC({
        onSuccess: onSuccessRef.current,
      }),
    }),
    [],
  );

  const questionProjection = useMemo(() => {
    if (!androidInAppBrowser) {
      return question;
    }

    const uploadQuestions = question.children.filter(
      (childQuestion) => childQuestion.type === QuestionType.HOST_IMAGE_UPLOAD,
    );

    if (uploadQuestions.length <= 1) {
      return question;
    }

    const firstUploadQuestionId = uploadQuestions[0]?.id;

    const children = question.children.filter((childQuestion) => {
      return childQuestion.type !== QuestionType.HOST_IMAGE_UPLOAD || childQuestion.id === firstUploadQuestionId;
    });

    return { ...question, children: children as ScreenProjection["children"] };
  }, [androidInAppBrowser, question]);

  return (
    <QuestionItemProvider questionItems={questionItems}>
      <QuestionDescriptionProvider
        domain={DOMAIN}
        i18nPrefix={PREFIX}
        portalHostName="StyleProfile"
        questionDescriptions={questionDescriptions}
        segment={segment}
        tradename={tradename}
        onOpenSilhouetteGuide={onOpenSilhouetteGuide}
        onOpenSizeGuide={onOpenSizeGuide}
      >
        <Question
          key={questionProjection.id}
          country={country}
          domain={DOMAIN}
          question={questionProjection}
          questionParentId=""
          segment={segment}
          tradename={tradename}
        />
      </QuestionDescriptionProvider>
    </QuestionItemProvider>
  );
};

export { QuestionForm };
